import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { LoginData, RegisterPayload, User } from '@/lib/types'
import type {
  GenerationOnloadResponse,
  GenerationSubmitPayload,
  GenerationSubmitResponse,
  GenerationHistoryResponse,
  GenerationDetailDTO,
  QueueUpdatesResponse,
  GalleryResponse,
  LoraImportRequest,
  LoraImportResponse,
  ListLorasResponse,
  UpscaleOnloadResponse,
  UpscaleSubmitPayload,
  UpscaleSubmitResponse,
  UpscaleUploadResponse,
} from '@/lib/types'

// Refresh lock to prevent duplicate refresh calls
let refreshPromise: Promise<void> | null = null

// Enhanced fetch with automatic token refresh
async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  const response = await fetch(url, options)

  if (response.status !== 401 || url === '/api/auth/refresh') {
    return response
  }

  if (!refreshPromise) {
    refreshPromise = fetch('/api/auth/refresh', { method: 'POST' })
      .then((refreshResponse) => {
        if (!refreshResponse.ok) {
          throw new Error('Token refresh failed')
        }
      })
      .finally(() => {
        refreshPromise = null
      })
  }

  try {
    await refreshPromise
  } catch {
    return response
  }

  return fetch(url, options)
}

async function parseJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let detail = 'Request failed'
    try {
      const error = await response.json()
      detail = error.detail || error.message || detail
    } catch (err) {
      // ignore JSON parse errors
    }
    const e: any = new Error(detail)
    e.status = response.status
    throw e
  }
  return response.json() as Promise<T>
}

// API client functions
const apiClient = {
  async login(data: LoginData): Promise<User> {
    const response = await fetch('/api/auth/login/onsubmit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    const json = await parseJson<{ user: User }>(response)
    return json.user
  },

  async getCurrentUser(): Promise<User> {
    const response = await fetchWithAuth('/api/auth/me')
    return parseJson<User>(response)
  },

  async register(data: RegisterPayload): Promise<User> {
    const response = await fetch('/api/auth/register/onsubmit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    const json = await parseJson<{ user: User }>(response)
    return json.user
  },

  async logout(): Promise<void> {
    await fetchWithAuth('/api/auth/logout/onsubmit', { method: 'POST' })
  },

  async refreshToken(): Promise<void> {
    const response = await fetch('/api/auth/refresh', { method: 'POST' })
    if (!response.ok) {
      throw new Error('Token refresh failed')
    }
  },

  async resetRequest(email: string): Promise<void> {
    const response = await fetch('/api/auth/reset/onsubmit/request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
    await parseJson(response)
  },

  async resetConfirm(token: string, newPassword: string): Promise<void> {
    const response = await fetch('/api/auth/reset/onsubmit/confirm', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, new_password: newPassword }),
    })
    await parseJson(response)
  },

  async getPageData(page: string): Promise<any> {
    const response = await fetchWithAuth(`/api/${page}/onload`)
    return parseJson<any>(response)
  },

  async getGenerationOnload(): Promise<GenerationOnloadResponse> {
    const response = await fetchWithAuth('/api/generation/onload')
    return parseJson(response)
  },

  async submitGeneration(payload: GenerationSubmitPayload): Promise<GenerationSubmitResponse> {
    const response = await fetchWithAuth('/api/generation/onsubmit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    return parseJson(response)
  },

  async getGenerationHistory(params: { limit?: number; cursor?: string | null }): Promise<GenerationHistoryResponse> {
    const query = new URLSearchParams()
    if (params.limit) query.set('limit', String(params.limit))
    if (params.cursor) query.set('cursor', params.cursor)
    const response = await fetchWithAuth(`/api/generation/history/list?${query.toString()}`)
    return parseJson(response)
  },

  async getGenerationDetail(generationId: string, includeFull = false): Promise<GenerationDetailDTO> {
    const response = await fetchWithAuth(`/api/generation/${generationId}?include_full=${includeFull}`)
    return parseJson(response)
  },

  async toggleFavorite(assetId: string, action: 'add' | 'remove' | 'toggle' = 'toggle') {
    const response = await fetchWithAuth(`/api/generation/asset/${assetId}/favorite?action=${action}`, {
      method: 'POST',
    })
    return parseJson(response)
  },

  async regenerateGeneration(generationId: string): Promise<GenerationSubmitResponse> {
    const response = await fetchWithAuth(`/api/generation/${generationId}/regenerate`, {
      method: 'POST',
    })
    return parseJson(response)
  },

  async deleteGeneration(generationId: string): Promise<{ success: boolean; message: string }> {
    const response = await fetchWithAuth(`/api/generation/${generationId}`, {
      method: 'DELETE',
    })
    return parseJson(response)
  },

  async bulkDeleteGenerations(generationIds: string[]): Promise<{ success: boolean; deleted_count: number; failed_ids: string[]; message: string }> {
    const response = await fetchWithAuth('/api/generation/bulk-delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ generation_ids: generationIds }),
    })
    return parseJson(response)
  },

  async getQueueUpdates(): Promise<QueueUpdatesResponse> {
    const response = await fetchWithAuth('/api/queue/updates')
    return parseJson(response)
  },

  async clearQueue(): Promise<{ message: string; cleared_count: number }> {
    const response = await fetchWithAuth('/api/queue/clear', {
      method: 'POST',
    })
    return parseJson(response)
  },

  async removeQueueItem(jobId: string): Promise<{ message: string; removed_job_id: string }> {
    const response = await fetchWithAuth(`/api/queue/remove/${jobId}`, {
      method: 'POST',
    })
    return parseJson(response)
  },

  async getGalleryOnload(limit = 40): Promise<GalleryResponse> {
    const response = await fetchWithAuth(`/api/gallery/onload?limit=${limit}`)
    return parseJson(response)
  },

  async getGalleryModels(): Promise<string[]> {
    const response = await fetchWithAuth('/api/gallery/models')
    return parseJson(response)
  },

  async searchGallery(params: {
    limit?: number
    cursor?: string | null
    query?: string | null
    base_model?: string | null
    lora_id?: number | null
    favorites_only?: boolean
    media_type?: 'any' | 'image' | 'video' | 'edit' | 'upscale'
  }): Promise<GalleryResponse> {
    const searchParams = new URLSearchParams()
    if (params.limit) searchParams.set('limit', String(params.limit))
    if (params.cursor) searchParams.set('cursor', params.cursor)
    if (params.query) searchParams.set('query', params.query)
    if (params.base_model) searchParams.set('base_model', params.base_model)
    if (params.lora_id) searchParams.set('lora_id', String(params.lora_id))
    if (params.favorites_only) searchParams.set('favorites_only', 'true')
    if (params.media_type) searchParams.set('media_type', params.media_type)
    const response = await fetchWithAuth(`/api/gallery/search?${searchParams.toString()}`)
    return parseJson(response)
  },

  async listLoraFamilies() {
    const response = await fetchWithAuth('/api/loras/families')
    return parseJson(response)
  },

  async listLoras(params: { baseModelFamily?: string; limit?: number; offset?: number; search?: string }): Promise<ListLorasResponse> {
    const searchParams = new URLSearchParams()
    if (params.baseModelFamily) searchParams.set('base_model_family', params.baseModelFamily)
    if (params.limit) searchParams.set('limit', String(params.limit))
    if (params.offset) searchParams.set('offset', String(params.offset))
    if (params.search) searchParams.set('search', params.search)
    const response = await fetchWithAuth(`/api/loras/?${searchParams.toString()}`)
    return parseJson(response)
  },

  async getLoraDetail(loraId: number) {
    const response = await fetchWithAuth(`/api/loras/${loraId}`)
    return parseJson(response)
  },

  async importLora(payload: LoraImportRequest): Promise<LoraImportResponse> {
    const response = await fetchWithAuth('/api/loras/import', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    return parseJson(response)
  },

  async deleteLora(loraId: number): Promise<{ success: boolean; message: string }> {
    const response = await fetchWithAuth(`/api/loras/${loraId}`, {
      method: 'DELETE',
    })
    return parseJson(response)
  },

  async updateLoraNotes(loraId: number, notes: string) {
    const response = await fetchWithAuth(`/api/loras/${loraId}/notes`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notes }),
    })
    return parseJson(response)
  },

  async getUpscaleOnload(): Promise<UpscaleOnloadResponse> {
    const response = await fetchWithAuth('/api/upscale/onload')
    return parseJson(response)
  },

  async submitUpscale(payload: UpscaleSubmitPayload): Promise<UpscaleSubmitResponse> {
    const response = await fetchWithAuth('/api/upscale/onsubmit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    return parseJson(response)
  },

  async uploadUpscaleSource(file: File): Promise<UpscaleUploadResponse> {
    const formData = new FormData()
    formData.append('file', file)
    const response = await fetchWithAuth('/api/upscale/upload', {
      method: 'POST',
      body: formData,
    })
    return parseJson(response)
  },

  async getPendingUsers(): Promise<{ pending_users: Array<{ id: number; email: string; created_at: string }> }> {
    const response = await fetchWithAuth('/api/admin/approve-users/onload')
    return parseJson(response)
  },

  async handleUserApproval(userId: number, action: 'approve' | 'deny'): Promise<{ success: boolean; message: string }> {
    const response = await fetchWithAuth('/api/admin/approve-users/onsubmit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, action }),
    })
    return parseJson(response)
  },
}

// Export the api object for use in route loaders
export const api = {
  auth: apiClient,
  getPageData: apiClient.getPageData,
  generation: {
    onLoad: apiClient.getGenerationOnload,
    submit: apiClient.submitGeneration,
    history: apiClient.getGenerationHistory,
    detail: apiClient.getGenerationDetail,
    toggleFavorite: apiClient.toggleFavorite,
    regenerate: apiClient.regenerateGeneration,
    delete: apiClient.deleteGeneration,
    bulkDelete: apiClient.bulkDeleteGenerations,
  },
  queue: {
    updates: apiClient.getQueueUpdates,
    clear: apiClient.clearQueue,
    removeItem: apiClient.removeQueueItem,
  },
  gallery: {
    onLoad: apiClient.getGalleryOnload,
    models: apiClient.getGalleryModels,
    search: apiClient.searchGallery,
  },
  loras: {
    families: apiClient.listLoraFamilies,
    list: apiClient.listLoras,
    detail: apiClient.getLoraDetail,
    import: apiClient.importLora,
    delete: apiClient.deleteLora,
    updateNotes: apiClient.updateLoraNotes,
  },
  upscale: {
    onLoad: apiClient.getUpscaleOnload,
    submit: apiClient.submitUpscale,
    upload: apiClient.uploadUpscaleSource,
  },
  admin: {
    getPendingUsers: apiClient.getPendingUsers,
    handleUserApproval: apiClient.handleUserApproval,
  },
}

// Auth hooks
export function useAuth() {
  const queryClient = useQueryClient()

  const login = useMutation({
    mutationFn: apiClient.login,
    onSuccess: (user) => {
      queryClient.setQueryData(['user'], user)
    },
  })

  const register = useMutation({
    mutationFn: apiClient.register,
    onSuccess: (user) => {
      queryClient.setQueryData(['user'], user)
    },
  })

  const logout = useMutation({
    mutationFn: apiClient.logout,
    onSuccess: () => {
      queryClient.clear()
    },
  })

  const refresh = useMutation({
    mutationFn: apiClient.refreshToken,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] })
    },
  })

  const userQuery = useQuery({
    queryKey: ['user'],
    queryFn: apiClient.getCurrentUser,
    retry: false,
  })

  return {
    login,
    register,
    logout,
    refresh,
    user: userQuery.data,
    isAuthenticated: !!userQuery.data && !userQuery.isError,
    isLoading: userQuery.isLoading
  }
}

// Page data hook
export function usePageData(page: string) {
  return useQuery({
    queryKey: ['pageData', page],
    queryFn: () => apiClient.getPageData(page),
    retry: (failureCount, error: any) => {
      if (error?.status === 401) return false
      return failureCount < 3
    }
  })
}

// Proactive token refresh hook
// Refreshes the access token at 80% of its TTL (12 minutes for 15 min TTL)
export function useProactiveRefresh() {
  const { user, isAuthenticated } = useAuth()

  useEffect(() => {
    if (!isAuthenticated || !user) return

    // Refresh at 80% of the 15 minute TTL = 12 minutes = 720000ms
    const refreshInterval = 12 * 60 * 1000

    const interval = setInterval(async () => {
      try {
        await apiClient.refreshToken()
      } catch (error) {
        console.error('Proactive token refresh failed:', error)
      }
    }, refreshInterval)

    return () => clearInterval(interval)
  }, [isAuthenticated, user])
}
